import { computed, inject, ref } from 'vue';
import { defineStore } from 'pinia';
import baseAxios from 'axios';
import _ from 'lodash';

import { AXIOS } from '../const';

import * as api from '../router/endpoints';

export const useAutotableStore = defineStore('autotable', () => {
  const axios = inject(AXIOS) || baseAxios;

  const variableTypeList = ref({
    text: 'text',
    double: 'double',
    boolean: 'boolean',
    identificator: 'identificator',
    date: 'date',
    line: 'line',
    point: 'point',
    polygon: 'polygon',
    geometry: 'geometry',
    document: 'document',
    array: 'array'
  });

  //common data
  const getAvailableVarsRequest = ref({});
  const getAvailableOutputVarsRequest = ref({});
  const getOperationsRequest = ref({});

  //autotable data
  const getAutotableRequest = ref({});
  const instructionSteps = ref([]);
  const customVariables = ref([]);
  const outputEntities = ref([]);

  const updateAutotableRequest = ref({});

  const instructionStepsObject = computed(() => _.keyBy(instructionSteps.value, 'id'));

  function defaultGetRequest(url, reqRef) {
    reqRef.value = {};
    reqRef.value.isLoading = true;

    const request = axios.get(url);

    request
      .then((resp) => (reqRef.value.data = resp.data))
      .catch((err) => (reqRef.value.error = err))
      .finally(() => {
        reqRef.value.isFinished = true;
        reqRef.value.isLoading = false;
      });

    return request;
  }

  function getAvailableVars(projectId, bpId, nodeId, inputTableId) {
    return defaultGetRequest(
      api.autotableVariables({
        inputTableId,
        projectId,
        nodeId,
        bpId
      }),
      getAvailableVarsRequest
    );
  }

  function getAvailableOutputVars(projectId, bpId, nodeId, inputTableId) {
    return defaultGetRequest(
      api.autotableOutputVariables({
        inputTableId,
        projectId,
        nodeId,
        bpId
      }),
      getAvailableOutputVarsRequest
    );
  }

  function getOperations(projectId, bpId, nodeId, inputTableId) {
    return defaultGetRequest(
      api.autotableOperations({
        inputTableId,
        projectId,
        nodeId,
        bpId
      }),
      getOperationsRequest
    );
  }

  function getAutotable(projectId, bpId, nodeId, inputTableId) {
    getAvailableVars(projectId, bpId, nodeId, inputTableId);
    getAvailableOutputVars(projectId, bpId, nodeId, inputTableId);
    getOperations(projectId, bpId, nodeId, inputTableId);

    return defaultGetRequest(
      api.autotable({
        inputTableId,
        projectId,
        nodeId,
        bpId
      }),
      getAutotableRequest
    ).then((resp) => {
      getAutotableRequest.value = {
        ...getAutotableRequest.value,
        inputTableId,
        projectId,
        nodeId,
        bpId
      };
      customVariables.value = resp.data.custom_variables.map((item) => ({
        ...item
      }));
      outputEntities.value = resp.data.output_entities.map((item) => ({
        ...item
      }));
      extractInstructionSteps();
    });
  }

  function updateAutotable(projectId, bpId, nodeId, inputTableId) {
    updateAutotableRequest.value = {};
    const req = updateAutotableRequest.value;
    req.isLoading = true;

    const request = axios.post(
      api.autotable({
        inputTableId,
        projectId,
        nodeId,
        bpId
      }),
      {
        instruction_steps: instructionSteps.value,
        custom_variables: customVariables.value,
        output_entities: outputEntities.value
      }
    );
    request
      .then((resp) => (req.data = resp.data))
      .catch((err) => (req.error = err))
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });

    return request;
  }

  function extractInstructionSteps() {
    if (!getAutotableRequest.value?.data?.instruction_steps) return;

    instructionSteps.value = getAutotableRequest.value.data.instruction_steps;
    const stepsObj = _.keyBy(instructionSteps.value, 'id');
    instructionSteps.value.forEach((step) => {
      step.children.forEach(
        (childId) => (stepsObj[childId].parent_id = step.id)
      );
    });
  }

  function createOrUpdateCustomVar(varProps, arrIndex = null) {
    if (_.isNumber(arrIndex)) {
      customVariables.value[arrIndex] = varProps;
    } else {
      customVariables.value = [
        ...customVariables.value,
        {
          ...varProps,
          name: crypto.randomUUID()
        }
      ];
    }
  }

  function createOrUpdateInstructionStep(stepProps, updateStepId) {
    const oldState = instructionStepsObject.value[updateStepId];
    const stepId = updateStepId || crypto.randomUUID();

    if (!oldState?.condition?.var && stepProps.condition?.var) {
      const childrenCount = stepProps.children?.length || 0;

      if (childrenCount < 1 && !stepProps.condition.true_step_id) {
        stepProps.condition.true_step_id = createEmptyStep(stepId);
      } else {
        stepProps.condition.true_step_id = _.first(stepProps.children);
      }

      stepProps.condition.false_step_id = createEmptyStep(stepId);

      stepProps.children = _.uniq([
        ...(stepProps.children || []),
        stepProps.condition.true_step_id,
        stepProps.condition.false_step_id
      ]);
    }

    if (oldState?.condition && !stepProps.condition) {
      removeInstructionStep(oldState.condition.false_step_id);
    }

    if (updateStepId) {
      instructionSteps.value = instructionSteps.value.map((st) => {
        if (st.id !== updateStepId) return st;

        return {
          children: [],
          ...stepProps,
          parent_id: oldState.parent_id
        };
      });

      return;
    } else {
      const parent = instructionStepsObject.value[stepProps.parent_id];
      parent?.children.push(stepId);
    }

    instructionSteps.value = [
      ...instructionSteps.value,
      {
        children: [],
        ...stepProps,
        id: stepId
      }
    ];

    return stepId;
  }

  function collectIdsForRemove(stepId) {
    const step = instructionStepsObject.value[stepId];

    return step.children.reduce(
      (acc, c) => [...acc, ...collectIdsForRemove(c)],
      [stepId]
    );
  }

  function removeInstructionStep(stepId) {
    const step = instructionStepsObject.value[stepId];
    const parent = instructionStepsObject.value[step.parent_id];

    if (!parent) {
      instructionSteps.value = [];
      return;
    }

    parent.condition = null;
    parent.children = _.without(parent.children, stepId);

    const removeIds = collectIdsForRemove(stepId);
    instructionSteps.value = _.without(instructionSteps.value, ...removeIds);
  }

  function createEmptyStep(parentId) {
    return createOrUpdateInstructionStep({
      parent_id: parentId,
      operation_id: null,
      input_vars: {},
      output_vars: {},
      children: [],
      condition: null
    });
  }

  function createPrecedingStep(nodeId) {
    const instructionStepsObj = _.keyBy(instructionSteps.value, 'id');

    const current = instructionStepsObj[nodeId];
    const parent = instructionStepsObj[current.parent_id];

    current.parent_id = createEmptyStep(parent.id);

    const parentStr = JSON.stringify(parent);

    const newParent = JSON.parse(
      parentStr.replaceAll(nodeId, current.parent_id)
    );
    createOrUpdateInstructionStep(newParent, newParent.id);
  }

  function $reset() {
    getAutotableRequest.value = {};
  }

  return {
    variableTypeList,
    getAvailableVarsRequest,
    getAvailableOutputVarsRequest,
    getOperationsRequest,
    getAutotableRequest,
    instructionSteps,
    customVariables,
    outputEntities,
    updateAutotableRequest,

    getAvailableVars,
    getAvailableOutputVars,
    getOperations,
    getAutotable,
    updateAutotable,
    createOrUpdateCustomVar,
    createOrUpdateInstructionStep,
    createEmptyStep,
    removeInstructionStep,
    createPrecedingStep,

    $reset
  };
});
